import React, { useEffect, useMemo, useState } from 'react'
import { Modal, Button } from 'react-bootstrap'
import { useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { acknowledgeLiveMessage } from '../../store/misc/misc'
import * as appRoutes from '../../routes'
import { LiveMessage } from '../../store/misc/interfaces'
import { loadAcknowledgedLiveMessages, saveAcknowledgedLiveMessages } from '../../store/localStorage'


const severityToVariant = (sev: LiveMessage['severity']) => {
  switch (sev) {
    case 'error':
      return 'danger'
    case 'warning':
      return 'warning'
    default:
      return 'info'
  }
}

// Build a deterministic key for a message so we can store acknowledgements
const messageKey = (m: LiveMessage) => `${m.title}::${m.description}`

const currentStageKey = (pathname: string): string | null => {
  const entries = [
    appRoutes.EVENTS,
    appRoutes.REGISTRATIONS,
    appRoutes.PARTICIPANT,
    appRoutes.PAYMENT,
    appRoutes.VERIFY,
    appRoutes.SUCCESS,
  ]
  const match = entries.find((e) => pathname.startsWith(e.path))
  return match ? match.key : null
}

export const LiveMessagesModal = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const [acknowledged, setAcknowledged] = useState<string[]>([])

  const { liveMessages } = useSelector((state: RootState) => state.misc)

  // Load acknowledgements from localStorage once
  useEffect(() => {
    setAcknowledged(loadAcknowledgedLiveMessages())
  }, [])

  const stage = useMemo(() => currentStageKey(location.pathname), [location.pathname])

  // Clear temporary acknowledgements when stage changes
  useEffect(() => {
    setAcknowledged((prev) => {
      const persisted = loadAcknowledgedLiveMessages()
      // Keep only those that are in persisted storage
      return prev.filter(key => persisted.includes(key))
    })
  }, [stage])

  const visibleMessage = useMemo(() => {
    if (!stage) return null
    const candidates = liveMessages.filter((m) => Array.isArray(m.stages) && m.stages!.includes(stage))
    const unack = candidates.find((m) => !acknowledged.includes(messageKey(m)))
    return unack || null
  }, [liveMessages, stage, acknowledged])

  const onAcknowledge = () => {
    if (!visibleMessage) return
    const key = messageKey(visibleMessage)
    const next = [...acknowledged, key]
    setAcknowledged(next)

    if (!visibleMessage.alwaysShow) {
      saveAcknowledgedLiveMessages(next)
      dispatch(acknowledgeLiveMessage(key))
    }
  }

  if (!visibleMessage) return null

  const variant = severityToVariant(visibleMessage.severity)

  return (
    <Modal show onHide={onAcknowledge} backdrop="static" keyboard={false}>
      <Modal.Header closeButton closeLabel={'Meldung schließen'} className={`bg-${variant} text-light`}>
        <Modal.Title>{visibleMessage.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div>{visibleMessage.description}</div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant={variant} onClick={onAcknowledge}>
          Verstanden
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default LiveMessagesModal
